import { create } from 'zustand';

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  tenantId: number;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  login: (user: User, token: string, refreshToken: string) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
}

// Load from localStorage on init
const loadAuthState = (): Partial<AuthState> => {
  try {
    const stored = localStorage.getItem('auth-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        user: parsed.user || null,
        token: parsed.token || null,
        refreshToken: parsed.refreshToken || null,
        isAuthenticated: parsed.isAuthenticated || false
      };
    }
  } catch (error) {
    console.error('Error loading auth state:', error);
  }
  return {
    user: null,
    token: null,
    refreshToken: null,
    isAuthenticated: false
  };
};

const saveAuthState = (state: AuthState) => {
  try {
    localStorage.setItem('auth-storage', JSON.stringify({
      user: state.user,
      token: state.token,
      refreshToken: state.refreshToken,
      isAuthenticated: state.isAuthenticated
    }));
  } catch (error) {
    console.error('Error saving auth state:', error);
  }
};

export const useAuthStore = create<AuthState>((set) => {
  const initialState = loadAuthState();
  
  return {
    ...initialState as AuthState,
    login: (user, token, refreshToken) => {
      const newState = {
        user,
        token,
        refreshToken,
        isAuthenticated: true
      };
      set(newState);
      saveAuthState(newState);
    },
    logout: () => {
      const newState = {
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false
      };
      set(newState);
      localStorage.removeItem('auth-storage');
    },
    updateUser: (updatedUser) => {
      set((state) => {
        const newState = {
          ...state,
          user: state.user ? { ...state.user, ...updatedUser } : null
        };
        saveAuthState(newState);
        return newState;
      });
    }
  };
});

